import { sortBy } from 'lodash';
import { AnyAction } from 'redux';
import { createReducer } from 'redux-create-reducer';
import {
  AddUserRoleNotification,
  AssignUserRolesRelations,
  AssignUserRolesRelationsResetToastState,
  ChangeProductField,
  ChangeTextField,
  ClearAssignUserRolesDrawerData,
  ClearCustomRoleStepper,
  ClearEditingUserRole,
  ClearRolesTableConfigData,
  ClearSelectedUserRoles,
  CreateCustomRole,
  DeleteUserRole,
  DiscardCustomRoleStepper,
  FetchCustomRole,
  FetchProductPermissions,
  FetchProductsCustomRole,
  FetchRoleToGroupsRelations,
  FetchRoleToUsersRelations,
  FetchUserRoles,
  HideUserRoleNotification,
  SetActiveEditCustomRoleId,
  SetAssignUserRolesDrawerData,
  SetCustomRoleDirty,
  SetCustomRoleStepperStep,
  SetDeleteCustomUserRole,
  SetEditingUserRole,
  SetPermissionTableSearch,
  SetPermissionTableSelectedRows,
  SetPermissionTableSelectedScopes,
  SetRolesTableConfigData,
  SetSelectedUserRoles,
  ToggleDeleteUserRoleModal,
  ToggleDiscardChangesModal,
  ToggleSummaryStepperBanner,
  UnassignGroupFromRole,
  UnassignGroupFromRoleResetToastState,
  UnassignUserFromRole,
  UnassignUserFromRoleResetToastState,
  UpdateCustomRole,
  ClearSelectedUserRolesToastData,
  setSelectedAppPermissions,
  FetchApplicationScopes,
  setSelectedCapabilitiesScopes,
  SetApplicationScopeSearchTerm,
  ClearApplicationScopesSearch,
  AddRolesTelemetryErrorNotification,
  SetRolesTypeSelectedFilter,
  SetRolesProductTypeSelectedFilter,
  ClearSelectedUserRolesTableFilters,
} from './actions';
import {
  IAssignUserRolesDrawerData,
  IUserRoleResp,
  IUserRolesState,
} from '../../interfaces/IUserRoles';
import { TABLE_CONFIG } from '../../utils/constant';

export const initialState: IUserRolesState = {
  selectedUserRoles: {
    items: [],
    toastData: null,
    loading: false,
    error: null,
  },
  rolesTableConfigData: {
    filteredUserRoles: [],
    productFilter: null,
    typeFilter: null,
    availableProducts: [],
    availableTypes: [],
    globalSearch: '',
    currentSorting: [
      {
        id: 'roleDisplayName',
        desc: false,
      },
    ],
    pagination: {
      pageIndex: 0,
      pageSize: TABLE_CONFIG.paginationConfig.pageSize,
    },
    selectedRolesRows: {},
  },
  editingUserRole: {
    id: '',
    product: '',
    roleDisplayName: '',
    isProductProvisioned: false,
    delegatable: false,
    assignUsersSearchField: '',
    assignUsersPagination: { pageIndex: 0, pageSize: 30 },
    assignUsersSortingOptions: [{ id: '', desc: false }],
    assignGroupsSearchField: '',
    assignGroupsPagination: { pageIndex: 0, pageSize: 30 },
    assignGroupsSortingOptions: [{ id: '', desc: false }],
    assignPermissionsSearchField: '',
    assignProductRolesSearchField: '',
    featureFlags: [],
    applicationScopeIdToScope: {},
    capabilitiesSearchField: '',
    selectedAbility: '',
    isEditable: false,
  },
  assignUserRolesDrawerData: {
    currentStep: 0,
    drawerUserOptions: [],
    drawerGroupOptions: [],
    selectedInstancesIds: {},
  },
  unassignUserFromRole: {
    loading: false,
    toastData: null,
    error: null,
  },
  unassignGroupFromRole: {
    loading: false,
    toastData: null,
    error: null,
  },
  userRoles: {
    items: [],
    loading: false,
    error: null,
  },
  roleToUsersRelations: {
    items: [],
    loading: false,
    error: null,
  },
  roleToGroupsRelations: {
    items: [],
    loading: false,
    error: null,
  },
  notification: {
    type: null,
    data: null,
    options: {},
  },
  errorEventData: {
    type: null,
    roleId: null,
    roleName: null,
  },
  customRoles: {
    isDeleteUserRoleModalVisible: false,
    productPermissions: [],
    applicationScopes: [],
    isDiscardChangesModalVisible: false,
    isLoading: false,
    readyToDisplay: false,
    error: null,
    permissionTableSearch: '',
    permissionTableSelectedRows: {},
    permissionTableSelectedScopes: {},
    capabilitiesTableSelectedScopes: { items: [], loading: false, error: '' },
    activeEditCustomRoleId: null,
    fetchedEditCustomRole: null,
    toggleSummaryStepperBanner: false,
    isCustomRoleDirty: false,
    customRoleProducts: [],
    deleteCustomUserRole: {
      isLoading: false,
      error: null,
      id: '',
      product: '',
      roleDisplayName: '',
      assignUsersSearchField: '',
      assignUsersPagination: { pageIndex: 0, pageSize: 30 },
      assignGroupsSearchField: '',
      assignGroupsPagination: { pageIndex: 0, pageSize: 30 },
      assignPermissionsSearchField: '',
    },
    stepper: {
      stepperStep: null,
      productOptions: {
        items: [],
        isLoading: false,
        error: null,
      },
      productField: {
        value: null,
        touched: false,
        focused: false,
        error:
          'userRoles:customRoleStepper.roleDetailsStep.errorMessages.emptyProduct',
      },
      roleNameField: {
        value: '',
        touched: false,
        focused: false,
        error:
          'userRoles:customRoleStepper.roleDetailsStep.errorMessages.emptyRoleName',
      },
      roleDescriptionField: {
        value: '',
        touched: false,
        focused: false,
        error: '',
      },
      productCapabilities: {
        items: [
          {
            productScope: {
              items: {
                options: [],
                label: '',
                searchTerm: '',
                isDirty: false,
                selectedOption: undefined,
                total: 0,
              },
              error: null,
              isLoading: false,
              touched: false,
              focused: false,
            },
            productPermission: {
              items: [],
              error: null,
              isLoading: false,
              touched: false,
              focused: false,
            },
          },
        ],
        error: '',
        loading: false,
      },
    },
  },
  rolesTableTypeFilterOption: null,
  rolesTableProductTypeFilterOption: null,
};

export default createReducer(initialState, {
  [FetchUserRoles.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    userRoles: {
      ...state.userRoles,
      loading: true,
      error: null,
    },
  }),
  [FetchUserRoles.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => {
    const sortedUserRolesItems: IUserRoleResp[] = sortBy(payload, [
      'roleDisplayName',
    ]);

    return {
      ...state,
      userRoles: {
        ...state.userRoles,
        items: sortedUserRolesItems,
        loading: false,
        error: null,
      },
    };
  },
  [FetchUserRoles.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    userRoles: {
      ...state.userRoles,
      items: [],
      loading: false,
      error: payload,
    },
  }),

  [FetchRoleToUsersRelations.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    roleToUsersRelations: {
      ...state.roleToUsersRelations,
      loading: true,
      error: null,
    },
  }),
  [FetchRoleToUsersRelations.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    roleToUsersRelations: {
      ...state.roleToUsersRelations,
      items: payload,
      loading: false,
      error: null,
    },
  }),
  [FetchRoleToUsersRelations.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    roleToUsersRelations: {
      ...state.roleToUsersRelations,
      items: [],
      loading: false,
      error: payload,
    },
  }),

  [UnassignUserFromRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    unassignUserFromRole: {
      ...state.unassignUserFromRole,
      loading: true,
      toastData: null,
      error: null,
    },
  }),
  [UnassignUserFromRole.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    unassignUserFromRole: {
      ...state.unassignUserFromRole,
      loading: false,
      toastData: payload,
      error: null,
    },
  }),
  [UnassignUserFromRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    unassignUserFromRole: {
      ...state.unassignUserFromRole,
      loading: false,
      error: payload,
      toastData: null,
    },
  }),
  [UnassignUserFromRoleResetToastState.TYPE]: (state: IUserRolesState) => ({
    ...state,
    unassignUserFromRole: {
      ...state.unassignUserFromRole,
      toastData: null,
    },
  }),

  [UnassignGroupFromRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    unassignGroupFromRole: {
      ...state.unassignGroupFromRole,
      loading: true,
      toastData: null,
      error: null,
    },
  }),
  [UnassignGroupFromRole.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    unassignGroupFromRole: {
      ...state.unassignGroupFromRole,
      loading: false,
      toastData: payload,
      error: null,
    },
  }),
  [UnassignGroupFromRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    unassignGroupFromRole: {
      ...state.unassignGroupFromRole,
      loading: false,
      error: payload,
      toastData: null,
    },
  }),
  [UnassignGroupFromRoleResetToastState.TYPE]: (state: IUserRolesState) => ({
    ...state,
    unassignGroupFromRole: {
      ...state.unassignGroupFromRole,
      toastData: null,
    },
  }),

  [FetchRoleToGroupsRelations.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    roleToGroupsRelations: {
      ...state.roleToGroupsRelations,
      loading: true,
      error: null,
    },
  }),
  [FetchRoleToGroupsRelations.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    roleToGroupsRelations: {
      ...state.roleToGroupsRelations,
      items: payload,
      loading: false,
      error: null,
    },
  }),
  [FetchRoleToGroupsRelations.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    roleToGroupsRelations: {
      ...state.roleToGroupsRelations,
      loading: false,
      error: payload,
    },
  }),

  [AssignUserRolesRelations.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    selectedUserRoles: {
      ...state.selectedUserRoles,
      loading: true,
      error: null,
    },
  }),
  [AssignUserRolesRelations.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => {
    const {
      selectedGroupsAmount,
      selectedUsersAmount,
      hasErrors,
      errors,
      rolesNamesText,
      selectedRolesCount,
    } = payload;

    return {
      ...state,
      selectedUserRoles: {
        ...state.selectedUserRoles,
        items: [],
        toastData: {
          groupsAmount: selectedGroupsAmount,
          usersAmount: selectedUsersAmount,
          selectedRolesCount,
          hasErrors,
          errors,
          rolesNamesText,
        },
        loading: false,
        error: null,
      },
      assignUserRolesDrawerData: {
        ...initialState.assignUserRolesDrawerData,
      },
    };
  },
  [AssignUserRolesRelations.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    selectedUserRoles: {
      ...state.selectedUserRoles,
      loading: false,
      error: payload,
    },
  }),
  [AssignUserRolesRelationsResetToastState.TYPE]: (state: IUserRolesState) => ({
    ...state,
    selectedUserRoles: {
      ...state.selectedUserRoles,
      toastData: null,
    },
  }),

  [SetSelectedUserRoles.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    selectedUserRoles: {
      ...state.selectedUserRoles,
      items: payload,
    },
  }),
  [ClearSelectedUserRoles.TYPE]: (state: IUserRolesState) => ({
    ...state,
    rolesTableConfigData: {
      ...state.rolesTableConfigData,
      selectedRolesRows: {},
    },
    selectedUserRoles: {
      ...state.selectedUserRoles,
      items: [],
      toastData: null,
    },
  }),
  [ClearSelectedUserRolesTableFilters.TYPE]: (state: IUserRolesState) => ({
    ...state,
    rolesTableConfigData: {
      ...state.rolesTableConfigData,
      productFilter: null,
      typeFilter: null,
      globalSearch: '',
    },
  }),
  [ClearSelectedUserRolesToastData.TYPE]: (state: IUserRolesState) => ({
    ...state,
    selectedUserRoles: {
      ...state.selectedUserRoles,
      toastData: null,
    },
  }),

  [SetEditingUserRole.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    editingUserRole: {
      ...state.editingUserRole,
      ...payload,
    },
  }),
  [ToggleDiscardChangesModal.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isDiscardChangesModalVisible: payload,
    },
  }),

  [SetRolesTableConfigData.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    rolesTableConfigData: {
      ...state.rolesTableConfigData,
      ...payload,
    },
  }),
  [ClearRolesTableConfigData.TYPE]: (state: IUserRolesState) => ({
    ...state,
    rolesTableConfigData: {
      ...initialState.rolesTableConfigData,
    },
    selectedUserRoles: {
      ...initialState.selectedUserRoles,
    },
  }),

  [SetAssignUserRolesDrawerData.TYPE]: (
    state: IUserRolesState,
    { payload }: { payload: Partial<IAssignUserRolesDrawerData> }
  ) => ({
    ...state,
    assignUserRolesDrawerData: {
      ...state.assignUserRolesDrawerData,
      ...payload,
    },
  }),
  [ClearAssignUserRolesDrawerData.TYPE]: (state: IUserRolesState) => ({
    ...state,
    assignUserRolesDrawerData: {
      currentStep: 0,
      drawerUserOptions: [],
      drawerGroupOptions: [],
      selectedInstancesIds: {},
    },
  }),

  [ClearEditingUserRole.TYPE]: (state: IUserRolesState) => ({
    ...state,
    editingUserRole: {
      ...initialState.editingUserRole,
      roleDescription: undefined,
    },
  }),
  [ToggleDeleteUserRoleModal.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isDeleteUserRoleModalVisible: payload,
    },
  }),
  [AddUserRoleNotification.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    notification: {
      type: payload.type,
      data: payload.roleName || null,
      options: payload.options || {},
    },
  }),
  [AddRolesTelemetryErrorNotification.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    errorEventData: {
      type: payload.errorEventType,
      roleId: payload.roleId,
      roleName: payload.roleName,
    },
  }),
  [HideUserRoleNotification.TYPE]: (state: IUserRolesState) => ({
    ...state,
    notification: {
      ...initialState.notification,
    },
  }),
  [SetDeleteCustomUserRole.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      deleteCustomUserRole: {
        ...state.customRoles.deleteCustomUserRole,
        ...payload,
      },
    },
  }),
  [DeleteUserRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      deleteCustomUserRole: {
        ...state.customRoles.deleteCustomUserRole,
        isLoading: true,
      },
    },
  }),
  [DeleteUserRole.Success.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      deleteCustomUserRole: {
        ...state.customRoles.deleteCustomUserRole,
        isLoading: false,
      },
      isDeleteUserRoleModalVisible: false,
    },
  }),
  [DeleteUserRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      deleteCustomUserRole: {
        ...state.customRoles.deleteCustomUserRole,
        isLoading: false,
        error: payload,
      },
    },
  }),

  [FetchProductsCustomRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        productOptions: {
          ...state.customRoles.stepper.productOptions,
          isLoading: true,
        },
      },
    },
  }),

  [FetchProductsCustomRole.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      customRoleProducts: payload.customRoleProducts,
      stepper: {
        ...state.customRoles.stepper,
        productOptions: {
          items: payload.formattedOptions,
          isLoading: false,
          error: null,
        },
      },
    },
  }),

  [FetchProductsCustomRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      customRoleProducts: initialState.customRoles.customRoleProducts,
      stepper: {
        ...state.customRoles.stepper,
        productOptions: {
          items: initialState.customRoles.stepper.productOptions.items,
          isLoading: false,
          error: null,
        },
      },
      error: payload,
    },
  }),

  [FetchProductPermissions.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: true,
    },
  }),
  [FetchProductPermissions.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      productPermissions: payload,
      isLoading: false,
      error: null,
    },
  }),
  [FetchProductPermissions.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      productPermissions: [],
      isLoading: false,
      error: payload,
    },
  }),

  [FetchCustomRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: true,
    },
  }),
  [FetchCustomRole.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      permissionTableSelectedScopes: payload.tableScopes,
      fetchedEditCustomRole: payload.customRole,
      capabilitiesTableSelectedScopes: payload.productCapabilities,
      isLoading: false,
      error: null,
      stepper: {
        ...state.customRoles.stepper,
        productField: {
          ...state.customRoles.stepper.productField,
          value: {
            ...state.customRoles.stepper.productField.value,
            label: payload.customRole.product,
            value: payload.customRole.productId,
            productKey: payload.customRole.productKey,
            selected: true,
          },
          error: '',
        },
        roleNameField: {
          ...state.customRoles.stepper.roleNameField,
          value: payload.customRole.roleDisplayName,
          error: '',
        },
        roleDescriptionField: {
          ...state.customRoles.stepper.roleDescriptionField,
          value: payload.customRole.roleDescription,
          error: '',
        },
        productCapabilities: {
          ...initialState.customRoles.stepper.productCapabilities,
          items: [...payload.productCapabilities.items],
        },
      },
    },
  }),
  [FetchCustomRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      permissionTableSelectedScopes: {},
      fetchedEditCustomRole: null,
      isLoading: false,
      error: payload,
      stepper: {
        ...state.customRoles.stepper,
        productField: initialState.customRoles.stepper.productField,
        roleNameField: initialState.customRoles.stepper.roleNameField,
        roleDescriptionField:
          initialState.customRoles.stepper.roleDescriptionField,
      },
    },
  }),

  [CreateCustomRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: true,
    },
  }),
  [CreateCustomRole.Success.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: false,
      error: null,
    },
  }),
  [CreateCustomRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: false,
      error: payload,
    },
  }),

  [UpdateCustomRole.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: true,
    },
  }),
  [UpdateCustomRole.Success.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: false,
      error: null,
    },
  }),
  [UpdateCustomRole.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: false,
      error: payload,
    },
  }),

  [SetPermissionTableSearch.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      permissionTableSearch: payload,
    },
  }),

  [SetRolesTypeSelectedFilter.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    rolesTableTypeFilterOption: payload,
  }),

  [SetRolesProductTypeSelectedFilter.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    rolesTableProductTypeFilterOption: payload,
  }),

  [SetPermissionTableSelectedRows.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      permissionTableSelectedRows: payload,
    },
  }),

  [SetPermissionTableSelectedScopes.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      permissionTableSelectedScopes: payload,
    },
  }),

  [ChangeProductField.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        productField: {
          ...state.customRoles.stepper.productField,
          ...payload,
        },
      },
    },
  }),
  [ChangeTextField.TYPE]: (state: IUserRolesState, { payload }: AnyAction) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        ...payload,
      },
    },
  }),
  [ClearCustomRoleStepper.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: initialState.customRoles,
  }),
  [SetActiveEditCustomRoleId.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      activeEditCustomRoleId: payload,
    },
  }),
  [DiscardCustomRoleStepper.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: initialState.customRoles,
    editingUserRole: initialState.editingUserRole,
  }),
  [SetCustomRoleStepperStep.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        stepperStep: payload,
      },
    },
  }),
  [ToggleSummaryStepperBanner.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      toggleSummaryStepperBanner: payload,
    },
  }),
  [SetCustomRoleDirty.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isCustomRoleDirty: payload,
    },
  }),
  [setSelectedAppPermissions.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        productCapabilities: {
          ...state.customRoles.stepper.productCapabilities,
          items: payload,
        },
      },
    },
  }),
  [FetchApplicationScopes.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      applicationScopes: payload,
      isLoading: false,
      readyToDisplay: true,
    },
  }),
  [FetchApplicationScopes.Pending.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      isLoading: true,
    },
  }),
  [FetchApplicationScopes.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      applicationScopes: [],
      isLoading: false,
      readyToDisplay: true,
      error: payload,
    },
  }),
  [SetApplicationScopeSearchTerm.Pending.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => {
    const { searchTerm, applicationScopeIndex } = payload;
    const mutatedItems = state.customRoles.stepper.productCapabilities.items;

    mutatedItems[applicationScopeIndex].productScope.items.searchTerm =
      searchTerm;
    mutatedItems[applicationScopeIndex].productScope.isLoading = true;
    mutatedItems[applicationScopeIndex].productScope.items.isDirty = true;

    return {
      ...state,
      customRoles: {
        ...state.customRoles,
        stepper: {
          ...state.customRoles.stepper,
          productCapabilities: {
            ...state.customRoles.stepper.productCapabilities,
            items: mutatedItems,
          },
        },
      },
    };
  },
  [SetApplicationScopeSearchTerm.Success.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => {
    const { applicationScopeIndex, response } = payload;
    const mutatedItems = state.customRoles.stepper.productCapabilities.items;

    mutatedItems[applicationScopeIndex].productScope.items.options = response;
    mutatedItems[applicationScopeIndex].productScope.isLoading = false;

    return {
      ...state,
      customRoles: {
        ...state.customRoles,
        stepper: {
          ...state.customRoles.stepper,
          productCapabilities: {
            ...state.customRoles.stepper.productCapabilities,
            items: mutatedItems,
          },
        },
      },
    };
  },
  [SetApplicationScopeSearchTerm.Error.TYPE]: (
    state: IUserRolesState,
    { payload }: AnyAction
  ) => {
    const { applicationScopeIndex, error } = payload;
    const mutatedItems = state.customRoles.stepper.productCapabilities.items;

    mutatedItems[applicationScopeIndex].productScope.items.options = [];
    mutatedItems[applicationScopeIndex].productScope.isLoading = false;

    return {
      ...state,
      customRoles: {
        ...state.customRoles,
        stepper: {
          ...state.customRoles.stepper,
          productCapabilities: {
            ...state.customRoles.stepper.productCapabilities,
            items: mutatedItems,
            error,
          },
        },
      },
    };
  },
  [setSelectedCapabilitiesScopes.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        productCapabilities: {
          ...state.customRoles.stepper.productCapabilities,
          items: [
            ...initialState.customRoles.stepper.productCapabilities.items,
            ...state.customRoles.capabilitiesTableSelectedScopes.items,
          ],
        },
      },
    },
  }),
  [ClearApplicationScopesSearch.TYPE]: (state: IUserRolesState) => ({
    ...state,
    customRoles: {
      ...state.customRoles,
      stepper: {
        ...state.customRoles.stepper,
        productCapabilities: {
          ...state.customRoles.stepper.productCapabilities,
          items: state.customRoles.stepper.productCapabilities.items.map(
            (item) => ({
              ...item,
              productScope: {
                ...item.productScope,
                items: {
                  ...item.productScope.items,
                  searchTerm: '',
                  options: item.edit
                    ? item.productScope.items.options
                    : state.customRoles.applicationScopes,
                },
              },
            })
          ),
        },
      },
    },
  }),
});
